import { computed, readonly, ref } from "vue";
import { NavigationFailure } from "vue-router";
import { TYPE } from "vue-toastification";
import { apiCall, logResponse, Response } from "@/core/api";
import { router } from "@/core/router";
import { showAlert } from "@/core/popup";
import { createCustomError, getSyncedItem } from "@/core/util";
import {
  changeOrganizationRole,
  getOrganizationRoles,
  OrganizationRole,
  organizationRoles,
  selectedOrganizationRole,
} from "@/core/auth/user-role";
import {
  checkPreferenceVersion,
  updateUserPreferences,
} from "@/core/preferences/preferences";
import i18n, { DEFAULT_LOCALE, SUPPORTED_LOCALES } from "@/core/i18n/i18n";

export interface AuthInfo {
  username: string;
  accessToken: string;
  refreshToken: string;
  authorities: any[];
  organizationId: string;
  tenantId: string;
  userId: string;
  roleId: string;
  preferences: any;
}

const { t } = i18n.global;
localStorage.removeItem("authInfo");
const authInfo = getSyncedItem<AuthInfo>("authInfo:tenant");

// OT-4948: localStorage.setItem() returning synchronously does not mean the write is durably
// committed to the browser's storage backend (a separate process in Chromium, reached over async
// IPC). window.location.reload() right after a synchronous setItem() can start the reloaded
// page's boot — including this very module's first read of authInfo — before that write lands,
// so the reload can still rehydrate the previous organization. The URL survives the reload
// exactly as written, with no such commit delay, so the switch carries its target organization
// there instead of relying on the store read racing the store write.
const ORG_SWITCH_QUERY_PARAM = "orgSwitch";

function consumePendingOrganizationSwitch(): void {
  const url = new URL(window.location.href);
  const pendingOrgId = url.searchParams.get(ORG_SWITCH_QUERY_PARAM);
  if (!pendingOrgId) {
    return;
  }
  url.searchParams.delete(ORG_SWITCH_QUERY_PARAM);
  window.history.replaceState(window.history.state, "", url.toString());

  if (authInfo.value) {
    authInfo.value.organizationId = pendingOrgId;
  }
}
consumePendingOrganizationSwitch();

const authInfoView = readonly(authInfo);
export { authInfoView as authInfo };

export const isUserLoggedIn = computed(() => authInfo.value !== undefined);

export const loadingOrganization = ref(false);

export const AuthError = createCustomError("AuthError");
export const TransientAuthError = createCustomError("TransientAuthError");

const TRANSIENT_RESPONSE_CODES = [408, 429, 502, 503, 504, 900, 901];

let refreshTokensPromise: Promise<void> | null = null;

function isTransientResponse(response: Response): boolean {
  return TRANSIENT_RESPONSE_CODES.includes(response.code);
}

export function isTransientAuthError(error: unknown): boolean {
  return error instanceof TransientAuthError;
}

function normalizeLocale(locale: unknown): string | undefined {
  const localeCandidate = Array.isArray(locale) ? locale[0] : locale;
  if (
    typeof localeCandidate === "string" &&
    SUPPORTED_LOCALES.includes(localeCandidate)
  ) {
    return localeCandidate;
  }
  return undefined;
}

function getActiveLocale(): string {
  return (
    normalizeLocale(router.currentRoute.value.params.locale) ||
    normalizeLocale(localStorage.getItem("locale")) ||
    DEFAULT_LOCALE
  );
}

export async function loginUsingSession(): Promise<boolean> {
  const response = await apiCall("POST", "/auth?useSession=true", {
    username: "",
    password: "",
  });

  return handleResponse(response);
}

export async function retrieveOAuth2Registrations(): Promise<any> {
  const response = await apiCall("GET", "/auth/sso-registration");

  if (response.code === 200) {
    return response.result;
  }

  return undefined;
}

export async function loginUsernamePassword(
  username: string,
  password: string
): Promise<any> {
  const response = await apiCall("POST", "/username-password", {
    username,
    password,
  });

  if (response.code === 200) {
    return response.result;
  }
  showAlert(TYPE.ERROR, response.message);
  return undefined;
}

export async function login(
  username: string,
  password: string
): Promise<boolean> {
  const response = await apiCall("POST", "/auth", {
    username,
    password,
  });

  return handleResponse(response);
}

async function handleResponse(response: Response) {
  if (response.code === 200) {
    authInfo.value = {
      username: response.result[0].username,
      accessToken: response.result[0].accessToken,
      refreshToken: response.result[0].refreshToken,
      authorities: response.result[0].authorities,
      organizationId: response.result[0].organizationId,
      tenantId: response.result[0].tenantId,
      userId: response.result[0].userId,
      roleId: undefined,
      preferences: checkPreferenceVersion(response.result[0].userPreferences),
    };
    const orgRoleResponse = await getOrganizationRoles(authInfo.value.userId);
    if (orgRoleResponse) {
      authInfo.value.organizationId =
        selectedOrganizationRole.value.organization.id;
      authInfo.value.roleId = selectedOrganizationRole.value.role.id;
      if (organizationRoles.value.length > 0) {
        return true;
      }
    }
    await logout();
    logResponse(response);
    return false;
  }

  if (response.code === 401 && response.message === "INVALID_CREDENTIALS") {
    showAlert(
      TYPE.ERROR,
      "You have entered an incorrect username or password."
    );
    return false;
  }
  if (response.code === 401 && response.message === "INVALID_TENANT") {
    showAlert(TYPE.ERROR, "Tenant not found.");
    return false;
  }
  if (response.code === 403 && response.message === "INACTIVE_USER") {
    showAlert(TYPE.ERROR, "User is not activated, please verify your email.");
    return false;
  }
  if (response.code === 400 && response.message === t("Auth.OrgIdNotPresent")) {
    showAlert(TYPE.ERROR, "OrgId is not present in the Request Header.");
    return false;
  }
  if (
    response.code === 400 &&
    response.message === t("Auth.TenantIdNotPresent")
  ) {
    showAlert(TYPE.ERROR, "TenantId is not present in the Request Header.");
    return false;
  }
  if (response.code === 500) {
    showAlert(TYPE.ERROR, "An unexpected error has occurred in the server.");
    return false;
  }
  logResponse(response);
  return false;
}

export async function logout(): Promise<void | NavigationFailure> {
  authInfo.value = undefined;
  organizationRoles.value = new Array<OrganizationRole>();
  selectedOrganizationRole.value = undefined;
  await router.push({
    name: "login",
    params: { locale: getActiveLocale() },
    query: { skipUnsavedCheck: "true" },
  });
}

/** Refreshes access and refresh tokens using the current refresh token.
 * @returns undefined if tokens are refreshed successfully, otherwise the error response
 */
export async function refreshTokens(): Promise<void> {
  if (refreshTokensPromise) {
    return refreshTokensPromise;
  }

  refreshTokensPromise = (async () => {
    const refreshToken = authInfo.value?.refreshToken;
    if (!refreshToken) {
      await logout();
      throw new AuthError(
        "Refresh token is missing. User needs to login again."
      );
    }

    const body = {
      token: refreshToken,
    };
    const response = await apiCall("POST", "/auth/refresh", body);

    if (response.code === 200) {
      if (!authInfo.value) {
        return;
      }

      authInfo.value.accessToken = response.result[0].accessToken;
      authInfo.value.refreshToken = response.result[0].refreshToken;
      authInfo.value.organizationId = response.result[0].organizationId;
      authInfo.value.tenantId = response.result[0].tenantId;
      authInfo.value.userId = response.result[0].userId;
      authInfo.value.preferences = checkPreferenceVersion(
        response.result[0].userPreferences
      );
      if (
        !selectedOrganizationRole.value &&
        organizationRoles.value.length === 0
      ) {
        const orgRoleResponse = await getOrganizationRoles(
          authInfo.value.userId,
          false
        );
        if (orgRoleResponse && selectedOrganizationRole.value) {
          authInfo.value.organizationId =
            selectedOrganizationRole.value.organization.id;
          authInfo.value.roleId = selectedOrganizationRole.value.role.id;
        } else {
          await logout();
        }
      }
      return;
    }

    logResponse(response);

    if (isTransientResponse(response)) {
      throw new TransientAuthError(
        "Tokens could not be refreshed because the server was unreachable."
      );
    }

    await logout();
    throw new AuthError(
      "Tokens could not be refreshed. User needs to login again."
    );
  })();

  try {
    return await refreshTokensPromise;
  } finally {
    refreshTokensPromise = null;
  }
}

export function isAccessTokenExpired(response: Response): boolean {
  return response.code === 401 && response.message === "INVALID_ACCESS_TOKEN";
}

/**
 * The authority of the built-in administrator role, matched exactly: it is the top of the
 * hierarchy the gateway declares, so nothing outranks it.
 */
const ADMIN_AUTHORITY = "ROLE_ADMIN";

/**
 * Whether the user administers the organization it is currently acting for — not the account as a
 * whole, since a user can administer one organization and be an ordinary member of another.
 *
 * Read from the same selected organization-role that {@link hasPermission} reads its privileges
 * from, and compared against the same authority the gateway checks, so a control this hides is a
 * control the gateway also refuses.
 */
export function isOrganizationAdmin(): boolean {
  return selectedOrganizationRole.value?.role?.authority === ADMIN_AUTHORITY;
}

export function hasPermission(permission: string): boolean {
  // OT-4895: guard against a poisoned/orphaned selected org-role missing its
  // role/privileges. Called from ~30 files (Sidebar, router guard, button gating);
  // an unguarded throw here can take down the whole app shell.
  if (selectedOrganizationRole.value?.role?.privileges) {
    const index = selectedOrganizationRole.value.role.privileges.findIndex(
      (item) => item.privilegeId === permission
    );
    return index !== -1;
  }
  return false;
}

export async function otpAuthenticate(otpCode: string): Promise<boolean> {
  const response = await apiCall("POST", "/otp/authenticate", {
    code: otpCode,
  });

  if (response.code === 200) {
    return true;
  }

  showAlert(TYPE.ERROR, response.message);
  return false;
}

export async function otpSetupRetrieveKey(): Promise<any | null> {
  return apiCall("GET", "/otp/setup-retrieve-key");
}

export async function otpSetupValidate(otpCode: string): Promise<boolean> {
  const response = await apiCall("POST", "/otp/setup-validate", {
    code: otpCode,
  });

  if (response.code === 200) {
    return true;
  }

  showAlert(TYPE.ERROR, response.message);
  return false;
}

export async function resetPassword(email: string): Promise<boolean> {
  const endpoint = `/users/reset-password/${email}`;
  const response = await apiCall("GET", endpoint);

  if (response.code === 200) {
    return true;
  }

  if (response.code === 401) {
    showAlert(TYPE.ERROR, response.message);
    return false;
  }

  logResponse(response);
  return false;
}

/** Appends the OT-4948 org-switch handoff param, preserving any query/hash the path already has. */
function withOrgSwitchParam(path: string, organizationId: string): string {
  const url = new URL(path, window.location.origin);
  url.searchParams.set(ORG_SWITCH_QUERY_PARAM, organizationId);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function isSelectedOrganizationRole(organizationRole: any): boolean {
  const selected = selectedOrganizationRole.value;
  return (
    !!selected &&
    !!organizationRole?.organization?.id &&
    !!organizationRole?.role?.id &&
    selected.organization?.id === organizationRole.organization.id &&
    selected.role?.id === organizationRole.role.id
  );
}

export async function setSelectedOrganizationRole(
  organizationRole: any,
  target?: string
): Promise<void> {
  if (isSelectedOrganizationRole(organizationRole)) {
    if (authInfo.value) {
      authInfo.value.organizationId = organizationRole.organization.id;
      authInfo.value.roleId = organizationRole.role.id;
    }
    if (target) {
      await router.push(
        withOrgSwitchParam(target, organizationRole.organization.id)
      );
      window.location.reload();
    }
    return;
  }

  loadingOrganization.value = true;
  try {
    await changeOrganizationRole(organizationRole).then(async (result) => {
      if (result) {
        authInfo.value.organizationId = organizationRole.organization.id;
        authInfo.value.roleId = organizationRole.role.id;
        await router.push({
          name: "dashboard",
          params: { locale: getActiveLocale() },
          query: {
            skipUnsavedCheck: "true",
            [ORG_SWITCH_QUERY_PARAM]: organizationRole.organization.id,
          },
        });
        if (target) {
          await router.push(
            withOrgSwitchParam(target, organizationRole.organization.id)
          );
        }
        window.location.reload();
      } else {
        showAlert(TYPE.ERROR, "Unable to switch organization.");
      }
    });
  } finally {
    loadingOrganization.value = false;
  }
}

export async function updatePreferences(preferences: any): Promise<boolean> {
  authInfo.value.preferences = preferences;
  return updateUserPreferences(authInfo.value.userId, preferences);
}

export async function updateSinglePreference(
  key: string,
  preferenceVal: any
): Promise<boolean> {
  const preferenceObj = authInfo.value.preferences[key];
  if (preferenceObj) {
    preferenceObj.value = preferenceVal;
    authInfo.value.preferences[key] = preferenceObj;
    const response = await await updateUserPreferences(
      authInfo.value.userId,
      authInfo.value.preferences
    );
    return Boolean(response);
  }
  return false;
}

export async function updateMultiplePreference(
  updatedPreferences: any
): Promise<boolean> {
  Object.entries(updatedPreferences).forEach(([key, value]: any[]) => {
    const preferenceObj = authInfo.value.preferences[key];
    if (preferenceObj) {
      preferenceObj.value = value || preferenceObj.default;
      authInfo.value.preferences[key] = preferenceObj;
    }
  });
  const response = await updateUserPreferences(
    authInfo.value.userId,
    authInfo.value.preferences
  );
  return !!response;
}
